import type { Chat, SearchResult } from '@liniya/api-client';
import { ListFilter, MessagesSquare, Plus, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ChatRow } from './ChatRow';
import { NewChatMenu } from './NewChatMenu';
import { UserSearch } from '@/features/search/UserSearch';
import { useOpenDirectChat } from '@liniya/client-core';
import { notify, useUiStore } from '@liniya/client-core';

export type ChatFilter = 'all' | 'direct' | 'group' | 'channel' | 'pinned';

const FILTER_VALUES: readonly ChatFilter[] = ['all', 'direct', 'group', 'channel', 'pinned'];

/**
 * Read the filter out of the address bar.
 *
 * The rail and the tab row are two controls over one piece of state, so that
 * state lives in the URL: keeping a copy in component state is what let the
 * rail highlight three buttons at once while filtering nothing.
 */
export function chatFilterFrom(params: URLSearchParams): ChatFilter {
  const value = params.get('filter');
  return FILTER_VALUES.includes(value as ChatFilter) ? (value as ChatFilter) : 'all';
}

const FILTER_LABELS: Array<{ value: ChatFilter; label: string }> = [
  { value: 'all', label: 'Все' },
  { value: 'direct', label: 'Личные' },
  { value: 'group', label: 'Группы' },
  { value: 'channel', label: 'Каналы' },
  // 'pinned' is deliberately absent: the tab row is four items wide by design
  // and a fifth would overflow the panel. The rail's star is its control.
];

interface Props {
  chats: Chat[];
  activeChatId: string | null;
  loading: boolean;
  error: boolean;
  onOpenChat: (chatId: string) => void;
  onRetry: () => void;
}

export function ChatListPanel({
  chats,
  activeChatId,
  loading,
  error,
  onOpenChat,
  onRetry,
}: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = chatFilterFrom(searchParams);
  const setFilter = (next: ChatFilter) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'all') params.delete('filter');
    else params.set('filter', next);
    setSearchParams(params, { replace: true });
  };
  const [query, setQuery] = useState('');
  const searchOpen = useUiStore((state) => state.searchOpen);
  const setSearchOpen = useUiStore((state) => state.setSearchOpen);
  const searchInput = useRef<HTMLInputElement>(null);
  const openDirect = useOpenDirectChat();

  useEffect(() => {
    if (searchOpen) searchInput.current?.focus();
  }, [searchOpen]);

  // Ctrl/Cmd+K opens search from anywhere, Escape closes it. Both are what
  // people already expect from a messenger, so they are wired once here.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setSearchOpen(true);
      }
      if (event.key === 'Escape' && searchOpen) {
        setSearchOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [searchOpen, setSearchOpen]);

  const visible = useMemo(() => {
    const byKind =
      filter === 'all'
        ? chats
        : filter === 'pinned'
          ? chats.filter((chat) => chat.membership.is_pinned)
          : chats.filter((chat) => chat.kind === filter);
    const text = query.trim().toLowerCase();
    if (!text) return byKind;
    return byKind.filter(
      (chat) =>
        chat.title.toLowerCase().includes(text) ||
        chat.last_message_preview.toLowerCase().includes(text),
    );
  }, [chats, filter, query]);

  const pickResult = async (result: SearchResult) => {
    try {
      if (result.type === 'user') {
        const chat = await openDirect.mutateAsync(result.id);
        onOpenChat(chat.id);
      } else {
        onOpenChat(result.id);
      }
      setQuery('');
      setSearchOpen(false);
    } catch {
      notify('Не удалось открыть чат.', 'error');
    }
  };

  return (
    <aside className="chat-list-panel">
      <header className="topbar list-head">
        <div className="brand">
          <strong>Ваши чаты</strong>
        </div>
        <div className="head-actions">
          <button
            className="icon-btn"
            type="button"
            aria-label="Поиск"
            title="Поиск (Ctrl K)"
            aria-expanded={searchOpen}
            onClick={() => setSearchOpen(!searchOpen)}
          >
            <Search size={20} />
          </button>
        </div>
      </header>

      {searchOpen ? (
        <label className="searchbox">
          <Search size={17} aria-hidden="true" />
          <input
            ref={searchInput}
            type="search"
            placeholder="Имя, username или email"
            aria-label="Поиск чатов и пользователей"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <kbd>ESC</kbd>
        </label>
      ) : null}

      {searchOpen && query.trim().length >= 2 ? (
        <UserSearch query={query} onPick={pickResult} busy={openDirect.isPending} />
      ) : null}

      <div className="tabs" role="tablist" aria-label="Фильтр чатов">
        {FILTER_LABELS.map((item) => (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={filter === item.value}
            className={filter === item.value ? 'tab active' : 'tab'}
            onClick={() => setFilter(item.value)}
          >
            {item.label}
            {item.value === 'all' && chats.length > 0 ? (
              <span className="badge" style={{ marginLeft: 6 }}>
                {chats.length}
              </span>
            ) : null}
          </button>
        ))}
        <span className="tab" style={{ marginLeft: 'auto' }} aria-hidden="true">
          <ListFilter size={18} />
        </span>
      </div>

      <div className="chat-list">
        {loading ? (
          <div aria-live="polite" aria-busy="true">
            {[0, 1, 2, 3, 4].map((index) => (
              <div
                key={index}
                className="skeleton"
                style={{ height: 62, margin: '8px 4px', borderRadius: 15 }}
              />
            ))}
          </div>
        ) : error ? (
          <div className="chat-list-empty" role="alert">
            <strong>Не удалось загрузить чаты</strong>
            <span>Проверьте подключение и попробуйте ещё раз.</span>
            <button type="button" className="secondary-btn" onClick={onRetry}>
              Повторить
            </button>
          </div>
        ) : visible.length === 0 ? (
          <div className="chat-list-empty">
            <MessagesSquare size={34} aria-hidden="true" />
            <strong>{query ? 'Ничего не найдено' : 'Пока нет чатов'}</strong>
            <span>
              {query
                ? 'Попробуйте изменить запрос.'
                : 'Создайте личный диалог через кнопку «+».'}
            </span>
          </div>
        ) : (
          visible.map((chat) => (
            <ChatRow
              key={chat.id}
              chat={chat}
              active={chat.id === activeChatId}
              onOpen={onOpenChat}
            />
          ))
        )}
      </div>

      {/*
        Anchored to the corner rather than the header: starting a chat is the
        one thing done from this panel, and on a phone the top of the screen is
        the hardest place on it to reach.
      */}
      <NewChatMenu onOpenChat={onOpenChat} variant="fab">
        <Plus size={24} />
      </NewChatMenu>
    </aside>
  );
}
